import { type ReactNode } from 'react';

import {
    type MessageArtifactRenderer,
    type MessageEntry,
    type RenderTimelineEntry,
    type ToolArtifactRenderer,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { InlineAdminArtifactChip } from '@apps/web/routes/_app/admin/agent/artifacts/fallback';
import {
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
    ArtifactLoadingStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    isInlineRenderableArtifact,
    resolveAdminArtifactRenderer,
} from '@apps/web/routes/_app/admin/agent/artifacts/registry';
import {
    type AdminArtifactRef,
    isRecord,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import { useAdminAgentRunArtifact } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export function createAdminToolArtifactRenderer(
    runId: string | null,
    options: { readonly suppressMessageArtifacts?: boolean } = {},
): ToolArtifactRenderer {
    return function (entry) {
        return (
            <AdminToolArtifactList
                entry={entry}
                runId={runId}
                suppressMessageArtifacts={
                    options.suppressMessageArtifacts ?? false
                }
            />
        );
    };
}

export function createAdminMessageArtifactRenderer(
    runId: string | null,
    options: {
        readonly onSelectArtifact?: (artifactId: string) => void;
    } = {},
): MessageArtifactRenderer {
    return function (message, entries) {
        return (
            <AdminMessageArtifactList
                message={message}
                runId={runId}
                entries={entries}
                onSelectArtifact={options.onSelectArtifact}
            />
        );
    };
}

export function AdminToolArtifactList({
    entry,
    runId,
    suppressMessageArtifacts = false,
}: {
    readonly entry: ToolExecutionEntry;
    readonly runId: string | null;
    readonly suppressMessageArtifacts?: boolean;
}) {
    const artifactRefs = suppressMessageArtifacts
        ? getArtifactRefs(entry.output).filter(
              (artifactRef) => !isMessageStreamArtifactRef(artifactRef),
          )
        : getArtifactRefs(entry.output);
    if (!runId || artifactRefs.length === 0) {
        return null;
    }
    return <AdminArtifactRefsList artifactRefs={artifactRefs} runId={runId} />;
}

function AdminMessageArtifactList({
    message,
    runId,
    entries,
    onSelectArtifact,
}: {
    readonly message: MessageEntry;
    readonly runId: string | null;
    readonly entries?: ReadonlyArray<RenderTimelineEntry>;
    readonly onSelectArtifact?: (artifactId: string) => void;
}) {
    if (!runId) {
        return null;
    }
    const artifactRefs = resolveMessageArtifactRefs(message, entries ?? []);
    if (artifactRefs.length === 0) {
        return null;
    }
    return (
        <AdminArtifactRefsList
            artifactRefs={artifactRefs}
            runId={runId}
            displayMode="inline"
            onSelectArtifact={onSelectArtifact}
        />
    );
}

function resolveMessageArtifactRefs(
    message: MessageEntry,
    entries: ReadonlyArray<RenderTimelineEntry>,
): ReadonlyArray<AdminArtifactRef> {
    if (message.artifactRefs && message.artifactRefs.length > 0) {
        return message.artifactRefs;
    }
    return hoistLegacyMessageArtifactRefs(message, entries);
}

function hoistLegacyMessageArtifactRefs(
    message: MessageEntry,
    entries: ReadonlyArray<RenderTimelineEntry>,
): ReadonlyArray<AdminArtifactRef> {
    const messageIndex = entries.findIndex(
        (entry) => entry.kind === 'message' && entry.id === message.id,
    );
    if (messageIndex < 0) {
        return [];
    }

    const artifactRefs: Array<AdminArtifactRef> = [];
    for (let index = messageIndex - 1; index >= 0; index -= 1) {
        const entry = entries[index];
        if (entry?.kind !== 'tool-execution') {
            break;
        }
        artifactRefs.unshift(
            ...getArtifactRefs(entry.output).filter(isMessageStreamArtifactRef),
        );
    }

    const dedupedArtifactRefs: Array<AdminArtifactRef> = [];
    const seenArtifactIds = new Set<string>();
    for (const artifactRef of artifactRefs) {
        if (seenArtifactIds.has(artifactRef.artifact_id)) {
            continue;
        }
        seenArtifactIds.add(artifactRef.artifact_id);
        dedupedArtifactRefs.push(artifactRef);
    }
    return dedupedArtifactRefs;
}

function AdminArtifactRefsList({
    artifactRefs,
    runId,
    displayMode = 'full',
    onSelectArtifact,
}: {
    readonly artifactRefs: ReadonlyArray<AdminArtifactRef>;
    readonly runId: string;
    readonly displayMode?: 'full' | 'inline';
    readonly onSelectArtifact?: (artifactId: string) => void;
}) {
    return (
        <div className="space-y-2">
            {artifactRefs.map((artifactRef) => (
                <AdminToolArtifactItem
                    key={artifactRef.artifact_id}
                    artifactRef={artifactRef}
                    runId={runId}
                    displayMode={displayMode}
                    onSelectArtifact={onSelectArtifact}
                />
            ))}
        </div>
    );
}

function AdminToolArtifactItem({
    artifactRef,
    runId,
    displayMode,
    onSelectArtifact,
}: {
    readonly artifactRef: AdminArtifactRef;
    readonly runId: string;
    readonly displayMode: 'full' | 'inline';
    readonly onSelectArtifact?: (artifactId: string) => void;
}) {
    const { data, error, isLoading } = useAdminAgentRunArtifact(
        runId,
        artifactRef.artifact_id,
    );
    const openArtifact = onSelectArtifact
        ? () => onSelectArtifact(artifactRef.artifact_id)
        : undefined;
    const isShiftCalendar =
        artifactRef.artifact_type === 'admin.shift_calendar';
    const wrapInline = (content: ReactNode): ReactNode =>
        displayMode === 'inline' && !isShiftCalendar ? (
            <InlineArtifactPreview onOpenArtifact={openArtifact}>
                {content}
            </InlineArtifactPreview>
        ) : (
            content
        );

    if (
        displayMode === 'inline' &&
        !isInlineRenderableArtifact(
            artifactRef.artifact_type,
            artifactRef.artifact_version,
        )
    ) {
        return (
            <InlineAdminArtifactChip
                artifactRef={artifactRef}
                onOpenArtifact={openArtifact}
            />
        );
    }

    if (isLoading && !data) {
        return wrapInline(
            <ArtifactFrame
                artifactRef={artifactRef}
                status={
                    artifactRef.lifecycle_status ? (
                        <ArtifactLifecycleStatusBadge
                            lifecycleStatus={artifactRef.lifecycle_status}
                        />
                    ) : (
                        <ArtifactLoadingStatusBadge />
                    )
                }
            />,
        );
    }
    if (error || !data) {
        return wrapInline(
            <ArtifactFrame
                artifactRef={artifactRef}
                status={
                    <span className="flex items-center gap-1.5 text-[11px] font-medium text-red-700">
                        <span className="size-1.5 shrink-0 rounded-full bg-red-600" />
                        Unavailable
                    </span>
                }
            >
                <p className="text-muted-foreground text-xs">
                    Could not load artifact details.
                </p>
            </ArtifactFrame>,
        );
    }

    const Renderer = resolveAdminArtifactRenderer(data);
    return wrapInline(
        <Renderer
            artifact={data}
            displayMode={displayMode}
            onOpenArtifact={openArtifact}
        />,
    );
}

function InlineArtifactPreview({
    children,
    onOpenArtifact,
}: {
    readonly children: ReactNode;
    readonly onOpenArtifact?: () => void;
}) {
    // Artifact content renders at natural size — the chat pane scrolls the
    // artifact into view rather than the artifact card scrolling internally.
    // See PHO-14022 (renderers) and PHO-14081 (wrappers).
    return (
        <div className="space-y-2" data-testid="admin-inline-artifact">
            {children}
            {onOpenArtifact ? (
                <button
                    type="button"
                    className="inline-flex items-center rounded-[3px] border border-[var(--aa-purple-border,#d8cffb)] bg-[var(--aa-purple-wash,#f7f5fe)] px-2 py-1 text-[11px] font-medium text-[var(--aa-purple-strong,#4629c7)] transition-colors hover:bg-[var(--aa-purple-soft,#eeeafd)] focus-visible:ring-2 focus-visible:ring-[var(--aa-focus,#5533ee)] focus-visible:outline-none"
                    onClick={onOpenArtifact}
                >
                    Open in Artifacts
                </button>
            ) : null}
        </div>
    );
}

export function getArtifactRefs(
    output: unknown,
): ReadonlyArray<AdminArtifactRef> {
    const data = toRecord(output);
    const refs: Array<AdminArtifactRef> = [];
    if (isArtifactRef(data['artifact_ref'])) {
        refs.push(data['artifact_ref']);
    }
    if (Array.isArray(data['artifacts'])) {
        for (const artifact of data['artifacts']) {
            if (isArtifactRef(artifact)) {
                refs.push(artifact);
            }
        }
    }
    const seen = new Set<string>();
    return refs.filter((ref) => {
        if (seen.has(ref.artifact_id)) {
            return false;
        }
        seen.add(ref.artifact_id);
        return true;
    });
}

function isArtifactRef(value: unknown): value is AdminArtifactRef {
    if (!isRecord(value)) {
        return false;
    }
    return (
        typeof value['artifact_id'] === 'string' &&
        typeof value['artifact_type'] === 'string' &&
        typeof value['title'] === 'string'
    );
}

function isMessageStreamArtifactRef(artifactRef: AdminArtifactRef): boolean {
    return isInlineRenderableArtifact(
        artifactRef.artifact_type,
        artifactRef.artifact_version,
    );
}
